import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  Settings,
  Users,
  Shield,
  ArrowRight,
  X,
  Volume2,
  Monitor,
  RefreshCw,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const MeetingLobby = ({ room, onJoin, onCancel }) => {
  const { user } = useAuth();
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [devices, setDevices] = useState({ video: [], audio: [] });
  const [selectedVideo, setSelectedVideo] = useState('');
  const [selectedAudio, setSelectedAudio] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [password, setPassword] = useState('');
  const [joining, setJoining] = useState(false);

  const videoRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    initializeMedia();
    loadDevices();

    return () => {
      cleanup();
    };
  }, []);

  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  const initializeMedia = async () => {
    try {
      setLoading(true);
      setError(null);

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: videoEnabled ? { width: 1280, height: 720 } : false,
        audio: audioEnabled
      });

      setStream(mediaStream);

      // Set up audio level monitoring
      if (audioEnabled) {
        setupAudioMonitor(mediaStream);
      }

      setLoading(false);
    } catch (err) {
      console.error('Media error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const loadDevices = async () => {
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = allDevices.filter(d => d.kind === 'videoinput');
      const audioDevices = allDevices.filter(d => d.kind === 'audioinput');

      setDevices({ video: videoDevices, audio: audioDevices });

      if (videoDevices.length > 0 && !selectedVideo) {
        setSelectedVideo(videoDevices[0].deviceId);
      }
      if (audioDevices.length > 0 && !selectedAudio) {
        setSelectedAudio(audioDevices[0].deviceId);
      }
    } catch (err) {
      console.error('Error loading devices:', err);
    }
  };

  const setupAudioMonitor = (mediaStream) => {
    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(mediaStream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;

      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const updateLevel = () => {
        if (analyserRef.current) {
          analyserRef.current.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((a, b) => a + b) / bufferLength;
          setAudioLevel(average / 255);
          animationRef.current = requestAnimationFrame(updateLevel);
        }
      };

      updateLevel();
    } catch (err) {
      console.error('Audio monitor error:', err);
    }
  };

  const cleanup = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
  };

  const toggleVideo = async () => {
    if (stream) {
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoEnabled;
        setVideoEnabled(!videoEnabled);
      }
    }
  };

  const toggleAudio = async () => {
    if (stream) {
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioEnabled;
        setAudioEnabled(!audioEnabled);
      }
    }
  };

  const handleJoin = async () => {
    setJoining(true);

    // Store media preferences for the video room
    window.visageMediaPrefs = {
      videoEnabled,
      audioEnabled,
      stream,
      password: password || undefined
    };

    onJoin();
  };

  const retryMedia = () => {
    cleanup();
    setStream(null);
    initializeMedia();
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-virgo-gold-400 to-amber-300 bg-clip-text text-transparent mb-2">
            Ready to join?
          </h1>
          <p className="text-virgo-teal-300 flex items-center justify-center gap-2">
            <Video className="w-5 h-5" />
            {room.name}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Video Preview */}
          <div className="bg-virgo-navy-700 rounded-2xl overflow-hidden border border-virgo-navy-600">
            <div className="aspect-video relative bg-virgo-navy-800">
              {loading ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-12 h-12 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-virgo-teal-300">Accessing camera...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="absolute inset-0 flex items-center justify-center p-6">
                  <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                    <p className="text-white font-medium mb-2">Camera access denied</p>
                    <p className="text-virgo-teal-400 text-sm mb-4">{error}</p>
                    <button
                      onClick={retryMedia}
                      className="flex items-center gap-2 px-4 py-2 bg-virgo-navy-600 text-white rounded-lg hover:bg-virgo-navy-500 transition mx-auto"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Retry
                    </button>
                  </div>
                </div>
              ) : videoEnabled ? (
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center text-white text-4xl font-semibold">
                    {user?.username?.charAt(0).toUpperCase() || 'U'}
                  </div>
                </div>
              )}

              {/* Name Badge */}
              <div className="absolute bottom-4 left-4 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-lg text-white text-sm flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                {user?.username || 'You'}
              </div>
            </div>

            {/* Media Controls */}
            <div className="p-4 flex items-center justify-center gap-4 bg-virgo-navy-700/50">
              <button
                onClick={toggleAudio}
                className={`p-4 rounded-full transition-all ${
                  audioEnabled
                    ? 'bg-virgo-navy-600 text-white hover:bg-virgo-navy-500'
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}
              >
                {audioEnabled ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
              </button>

              <button
                onClick={toggleVideo}
                className={`p-4 rounded-full transition-all ${
                  videoEnabled
                    ? 'bg-virgo-navy-600 text-white hover:bg-virgo-navy-500'
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}
              >
                {videoEnabled ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
              </button>

              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-4 rounded-full bg-virgo-navy-600 text-white hover:bg-virgo-navy-500 transition-all"
              >
                <Settings className="w-6 h-6" />
              </button>
            </div>

            {/* Audio Level Indicator */}
            {audioEnabled && (
              <div className="px-4 pb-4">
                <div className="flex items-center gap-3">
                  <Volume2 className="w-4 h-4 text-virgo-teal-400" />
                  <div className="flex-1 h-2 bg-virgo-navy-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all duration-75"
                      style={{ width: `${audioLevel * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Join Panel */}
          <div className="flex flex-col">
            {/* Room Info */}
            <div className="bg-virgo-navy-700 rounded-2xl p-6 border border-virgo-navy-600 mb-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Video className="w-5 h-5 text-cyan-400" />
                Meeting Details
              </h3>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-virgo-teal-400">Room</span>
                  <span className="text-white font-medium">{room.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-virgo-teal-400">Participants</span>
                  <span className="text-white flex items-center gap-1.5">
                    <Users className="w-4 h-4" />
                    {room.participant_count || 0} / {room.max_participants}
                  </span>
                </div>
                {room.require_password && (
                  <div className="flex items-center justify-between">
                    <span className="text-virgo-teal-400">Security</span>
                    <span className="text-amber-400 flex items-center gap-1.5">
                      <Shield className="w-4 h-4" />
                      Password Protected
                    </span>
                  </div>
                )}
              </div>

              {room.require_password && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-virgo-teal-400 mb-2">
                    Meeting Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="w-full px-4 py-3 bg-virgo-navy-800 border border-virgo-navy-600 rounded-xl text-white placeholder-virgo-navy-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              )}
            </div>

            {/* Device Settings */}
            {showSettings && (
              <div className="bg-virgo-navy-700 rounded-2xl p-6 border border-virgo-navy-600 mb-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-cyan-400" />
                  Device Settings
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-2">
                      Camera
                    </label>
                    <select
                      value={selectedVideo}
                      onChange={(e) => setSelectedVideo(e.target.value)}
                      className="w-full px-4 py-3 bg-virgo-navy-800 border border-virgo-navy-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    >
                      {devices.video.map((device) => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.label || `Camera ${devices.video.indexOf(device) + 1}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-400 mb-2">
                      Microphone
                    </label>
                    <select
                      value={selectedAudio}
                      onChange={(e) => setSelectedAudio(e.target.value)}
                      className="w-full px-4 py-3 bg-virgo-navy-800 border border-virgo-navy-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    >
                      {devices.audio.map((device) => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.label || `Microphone ${devices.audio.indexOf(device) + 1}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4 mt-auto">
              <button
                onClick={onCancel}
                className="flex-1 px-6 py-4 bg-virgo-navy-600 text-white rounded-xl hover:bg-virgo-navy-500 transition-all font-medium flex items-center justify-center gap-2"
              >
                <X className="w-5 h-5" />
                Cancel
              </button>

              <button
                onClick={handleJoin}
                disabled={joining || (room.require_password && !password)}
                className="flex-1 px-6 py-4 bg-gradient-to-r from-cyan-500 to-indigo-600 text-white rounded-xl hover:from-cyan-600 hover:to-indigo-700 transition-all font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-cyan-500/25"
              >
                {joining ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Joining...
                  </>
                ) : (
                  <>
                    Join Meeting
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>

            {/* Tips */}
            <div className="mt-6 p-4 bg-virgo-navy-700/50 rounded-xl border border-virgo-navy-600">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-virgo-teal-400">
                  <p className="font-medium text-white mb-1">Tips for a great meeting</p>
                  <ul className="space-y-1">
                    <li>• Use headphones to avoid echo</li>
                    <li>• Find a quiet, well-lit space</li>
                    <li>• Close unnecessary applications</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingLobby;
